import {
  ExperienceMapper,
  ExperienceEntryLike,
  AudienceMapper,
  AudienceEntryLike,
} from '@ninetailed/experience.js-utils-contentful';
import { createClient } from 'contentful';
import { contentfulConfig } from '../libs/contentful/contentful';
import { Sys } from 'libs/contentful/types';
import { getNinetailedMode } from 'libs/experimentation/requestContext';
import { ExtendedExperienceConfiguration } from 'libs/contentful/queries/ninetailed';
import { Reference } from '@ninetailed/experience.js-shared';

type ContentfulClient = ReturnType<typeof createClient>;

let productionClient: ContentfulClient | null = null;
let previewClient: ContentfulClient | null = null;

function getProductionClient(): ContentfulClient {
  if (!productionClient) {
    productionClient = createClient({
      space: contentfulConfig.spaceId,
      accessToken: contentfulConfig.accessToken,
      environment: contentfulConfig.environment,
    });
  }
  return productionClient;
}

function getPreviewClient(): ContentfulClient {
  if (!previewClient) {
    previewClient = createClient({
      space: contentfulConfig.spaceId,
      accessToken: contentfulConfig.previewAccessToken,
      environment: contentfulConfig.environment,
      host: 'preview.contentful.com',
    });
  }
  return previewClient;
}

export const getContentfulClient = (options?: { draftPreviewEnabled?: boolean }): ContentfulClient => {
  const isDraftPreviewEnabled = options?.draftPreviewEnabled ?? false;
  return isDraftPreviewEnabled ? getPreviewClient() : getProductionClient();
};

/**
 * Recursively removes circular references from an object
 * @param entry - The entry to clean
 * @param seen - Set of entries already seen
 * @returns A clean version of the object without circular references
 */
function removeCircularReferences(entry: unknown, seen = new WeakSet<object>()): unknown {
  if (entry === null || typeof entry !== 'object') {
    return entry;
  }

  if (seen.has(entry)) {
    const contentfulEntry = entry as { sys: Sys };
    return contentfulEntry?.sys ? { sys: { id: contentfulEntry.sys.id } } : {};
  }

  seen.add(entry);

  if (Array.isArray(entry)) {
    const result = entry.map(item => removeCircularReferences(item, seen));
    seen.delete(entry);
    return result;
  }

  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(entry as Record<string, unknown>)) {
    cleaned[key] = removeCircularReferences(value, seen);
  }

  seen.delete(entry);
  return cleaned;
}

// Experiences and audiences are only consumed by NinetailedPreviewPlugin in client mode.
// In server mode the data is discarded, so skip the Contentful fetch entirely.
export const getAllExperiences = async (options?: { draftPreviewEnabled?: boolean }) => {
  const ninetailedMode = await getNinetailedMode();
  if (ninetailedMode !== 'client') return [];

  const client = getContentfulClient(options);
  const entries = await client.getEntries({
    content_type: 'nt_experience',
    include: 1,
  });

  // Clean circular references to avoid errors when passed to the client-side plugin
  const processedEntries = entries.items.map(entry => removeCircularReferences(entry));

  return (processedEntries as unknown as ExperienceEntryLike[])
    .filter(ExperienceMapper.isExperienceEntry)
    .map(ExperienceMapper.mapExperience);
};

export const getAllAudiences = async (options?: { draftPreviewEnabled?: boolean }) => {
  const ninetailedMode = await getNinetailedMode();
  if (ninetailedMode !== 'client') return [];

  const client = getContentfulClient(options);
  const entries = await client.getEntries({
    content_type: 'nt_audience',
  });

  return (entries.items as unknown as AudienceEntryLike[])
    .filter(AudienceMapper.isAudienceEntry)
    .map(AudienceMapper.mapAudience)
    .map(audience => ({
      ...audience,
      name: audience.name || audience.id,
    }));
};

/**
 * Filters out null values from an array of experiences
 * @param experiences - The array of experiences to filter
 * @returns The filtered array of experiences
 */
export function filterValidExperiences<T extends Reference>(
  experiences: Array<ExtendedExperienceConfiguration<T> | null>,
): ExtendedExperienceConfiguration<T>[] {
  return experiences.filter((exp): exp is ExtendedExperienceConfiguration<T> => exp !== null);
}
